import DateSelect from "@/components/DateSelect";
import TimePicker from "@/components/TimePicker";
import { Controller, useFormContext } from "react-hook-form";
import classNames from "classnames";
import Icon from "@/components/Icon";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import MaxAttendanceField from "./MaxAttendanceField";
import Divider from "@/components/Divider";
import { useRef } from "react";
import { Date as EventDate, SessionFormData, SessionDate } from "../types.ts";

type SessionFieldName = "date" | "startTime" | "endTime";

interface SessionFieldsProps {
  hasMultipleSessions: boolean;
  index: number;
  timezone: string;
  onRemove: () => void;
  handleSessionChange: (nextSessions?: SessionFormData[]) => void;
  eventType?: { name: string };
  maxAttendanceDisabled?: boolean;
  maxAttendanceDisplayValue?: string;
}

interface SessionFormValues {
  sessions: {
    date: EventDate;
    startTime: string;
    endTime: string;
    maxAttendance?: number | null;
  }[];
}

const isFieldEmpty = (field: SessionFieldName, value: string | EventDate | null) => {
  if (field === "date") {
    return !value || !(value as unknown as EventDate).startDate;
  }
  return !value;
};

const SessionFields = ({
  hasMultipleSessions,
  index,
  onRemove,
  timezone,
  handleSessionChange,
  eventType,
  maxAttendanceDisabled = false,
  maxAttendanceDisplayValue
}: SessionFieldsProps) => {
  const {
    control,
    watch,
    setValue,
    trigger,
    clearErrors,
    formState: { errors }
  } = useFormContext<SessionFormValues>();
  const prevValues = useRef<{
    date: EventDate;
    startTime: string;
    endTime: string;
  }>({ date: { startDate: "", endDate: "" }, startTime: "", endTime: "" });
  const { isCompactLayout: isMobileView } = useIsCompactLayout();
  const allowPastDates = eventType?.name === "Recruitment";

  const handleFocus = (field: SessionFieldName) => {
    const currentValue = watch(`sessions.${index}.${field}`);

    if (isFieldEmpty(field, currentValue)) {
      return;
    }

    if (field === "date") {
      const dateValue = currentValue as EventDate;
      if (dateValue.startDate) {
        prevValues.current.date = dateValue;
      }
    } else {
      prevValues.current[field] = currentValue as string;
    }
  };

  const handleBlur = (field: SessionFieldName) => {
    const currentValue = watch(`sessions.${index}.${field}`);
    if (isFieldEmpty(field, currentValue)) {
      setValue(`sessions.${index}.${field}`, prevValues.current[field]);
    }

    if (field === "startTime" || field === "endTime") {
      trigger();
    }
  };

  const handleDateChange = (value: EventDate) => {
    const dateValue: SessionDate = {
      startDate: value.startDate ?? "",
      endDate: value.endDate ?? value.startDate ?? ""
    };
    setValue(`sessions.${index}.date`, dateValue);
    clearErrors();

    const currentSessions = watch("sessions") as SessionFormData[];
    const nextSessions: SessionFormData[] = currentSessions.map((session, sessionIndex) =>
      sessionIndex === index ? { ...session, date: dateValue } : session
    );
    handleSessionChange(nextSessions);
  };

  const renderMobileLayout = () => (
    <>
      {index > 0 && (
        <div className="py-3">
          <Divider />
        </div>
      )}
      <div className="flex flex-col gap-6 w-full">
        <div className="flex items-start justify-between">
          <div className="flex-grow">
            <Controller
              name={`sessions.${index}.date`}
              control={control}
              render={({ field }) => (
                <DateSelect
                  label="Date"
                  placeholder="Select Date"
                  displayFormat="MMM DD, YYYY"
                  value={field.value}
                  onChange={(value) => handleDateChange(value as EventDate)}
                  useSingleDate
                  popoverDirection="down"
                  minDate={allowPastDates ? undefined : new Date()}
                  startFrom={field.value?.startDate ? new Date(field.value.startDate) : undefined}
                  error={!!errors.sessions?.[index]?.date?.startDate}
                  errorMessage={errors.sessions?.[index]?.date?.startDate?.message}
                />
              )}
            />
          </div>
          {hasMultipleSessions && (
            <div className="h-[56px] flex items-center cursor-pointer p-2 ml-2" onClick={onRemove}>
              <Icon name="delete" />
            </div>
          )}
        </div>
        <div className="grid w-full min-w-0 grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-4">
          <Controller
            name={`sessions.${index}.startTime`}
            control={control}
            render={({ field }) => (
              <TimePicker
                label={`Start Time (${timezone})`}
                value={field.value || ""}
                onChange={(newTime) => {
                  field.onChange(newTime);
                  handleBlur("startTime");
                }}
                onFocus={() => handleFocus("startTime")}
                onBlur={() => handleBlur("startTime")}
                error={!!errors.sessions?.[index]?.startTime}
                errorMessage={errors.sessions?.[index]?.startTime?.message}
              />
            )}
          />
          <Controller
            name={`sessions.${index}.endTime`}
            control={control}
            render={({ field }) => (
              <TimePicker
                label={`End Time (${timezone})`}
                value={field.value || ""}
                onChange={field.onChange}
                onFocus={() => handleFocus("endTime")}
                onBlur={() => handleBlur("endTime")}
                error={!!errors.sessions?.[index]?.endTime}
                errorMessage={errors.sessions?.[index]?.endTime?.message}
              />
            )}
          />
        </div>
        {eventType?.name !== "Recruitment" && (
          <MaxAttendanceField
            index={index}
            disabled={maxAttendanceDisabled}
            displayValue={maxAttendanceDisplayValue}
            helperText={maxAttendanceDisabled ? "Set by global max registrants" : undefined}
          />
        )}
      </div>
    </>
  );

  const renderDesktopLayout = () => (
    <div className={classNames("relative flex lg:min-w-[680px]")}>
      <div className="grid gap-x-2 gap-y-6 w-full sm:grid-cols-2 lg:grid-cols-[repeat(3,minmax(160px,1fr))_minmax(180px,1.5fr)]">
        <Controller
          name={`sessions.${index}.date`}
          control={control}
          render={({ field }) => (
            <DateSelect
              label="Date"
              placeholder="Select Date"
              displayFormat="MMM DD, YYYY"
              value={field.value}
              onChange={(value) => handleDateChange(value as EventDate)}
              onFocus={() => handleFocus("date")}
              onBlur={() => handleBlur("date")}
              useSingleDate
              popoverDirection="down"
              minDate={allowPastDates ? undefined : new Date()}
              startFrom={field.value?.startDate ? new Date(field.value.startDate) : undefined}
              error={!!errors.sessions?.[index]?.date?.startDate}
              errorMessage={errors.sessions?.[index]?.date?.startDate?.message}
            />
          )}
        />
        <Controller
          name={`sessions.${index}.startTime`}
          control={control}
          render={({ field }) => (
            <TimePicker
              label={`Start Time (${timezone})`}
              value={field.value || ""}
              onChange={(newTime) => {
                field.onChange(newTime);
                handleBlur("startTime");
              }}
              onFocus={() => handleFocus("startTime")}
              onBlur={() => handleBlur("startTime")}
              error={!!errors.sessions?.[index]?.startTime}
              errorMessage={errors.sessions?.[index]?.startTime?.message}
            />
          )}
        />
        <Controller
          name={`sessions.${index}.endTime`}
          control={control}
          render={({ field }) => (
            <TimePicker
              label={`End Time (${timezone})`}
              value={field.value || ""}
              onChange={field.onChange}
              onFocus={() => handleFocus("endTime")}
              onBlur={() => handleBlur("endTime")}
              error={!!errors.sessions?.[index]?.endTime}
              errorMessage={errors.sessions?.[index]?.endTime?.message}
            />
          )}
        />
        {eventType?.name !== "Recruitment" && (
          <MaxAttendanceField
            index={index}
            disabled={maxAttendanceDisabled}
            displayValue={maxAttendanceDisplayValue}
            helperText={maxAttendanceDisabled ? "Set by global max registrants" : undefined}
          />
        )}
      </div>
      {hasMultipleSessions && (
        <div className="absolute -right-10 flex items-center md:h-[50px]">
          <div className="cursor-pointer w-6 h-6" onClick={onRemove}>
            <Icon name="delete" />
          </div>
        </div>
      )}
    </div>
  );

  return isMobileView ? renderMobileLayout() : renderDesktopLayout();
};

export default SessionFields;
